/**
 * WorkflowStepDialog — confirms a manual workflow-step override on a job.
 *
 * CR-2026-FE-WORKFLOW-OVERRIDE 2026-05-05. Companion to
 * CR-2026-BE-WORKFLOW-OVERRIDE on sfm-web-backend (route POST
 * /api/job/:id/detailing-workspace/workflow-step).
 *
 * Use:
 *   <WorkflowStepDialog
 *     open={Boolean(targetStep)}
 *     currentStep={wf.current}
 *     targetStep={targetStep}
 *     onCancel={() => setTargetStep(null)}
 *     onConfirm={(reason) => handleConfirm(reason)}
 *     submitting={submitting}
 *     error={submitError}
 *   />
 *
 * Audit-day defense: every override capture includes (a) actor identity,
 * (b) target step, (c) typed reason ≥4 chars, (d) timestamp. Recorded as
 * an immutable audit_log row protected by V-03 DB triggers. To "undo" an
 * override, post another override (this dialog) with the prior step as
 * the new target. The audit chain shows the back-and-forth.
 */

import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Alert, Stack, Chip, Box,
} from "@mui/material";
import { TimelineRounded } from "@mui/icons-material";
import { WORKFLOW_STEP_LABELS } from "../../utils/detailingWorkspaceApi";

export default function WorkflowStepDialog({
  open,
  currentStep,
  targetStep,
  onCancel,
  onConfirm,
  submitting = false,
  error = null,
}) {
  const [reason, setReason] = useState("");

  // Reset reason whenever the dialog opens for a new target
  useEffect(() => {
    if (open) setReason("");
  }, [open, targetStep]);

  const targetLabel = targetStep ? (WORKFLOW_STEP_LABELS[targetStep] || targetStep) : "";
  const currentLabel = currentStep ? (WORKFLOW_STEP_LABELS[currentStep] || currentStep) : "";
  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= 4;
  const sameStep = targetStep === currentStep;

  const handleSubmit = () => {
    if (!reasonValid || sameStep || submitting) return;
    onConfirm?.(trimmed);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}
      >
        <TimelineRounded color="primary" />
        Set workflow step
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: "0.04em", display: "block", mb: 0.5 }}>
              FROM
            </Typography>
            <Chip
              label={currentLabel || "—"}
              sx={{ bgcolor: "#f0f3f7", color: "text.secondary", fontWeight: 600 }}
            />
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: "0.04em", display: "block", mb: 0.5 }}>
              TO
            </Typography>
            <Chip
              label={targetLabel}
              sx={{ bgcolor: "primary.main", color: "white", fontWeight: 600, boxShadow: "0 0 0 3px rgba(0,102,204,.2)" }}
            />
          </Box>

          {sameStep && (
            <Alert severity="info">
              The job is already at <strong>{currentLabel}</strong>. Pick a different step to override.
            </Alert>
          )}

          <TextField
            label="Reason for override"
            placeholder="Why are you setting this step manually?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            required
            disabled={submitting}
            helperText={
              reason.length > 0 && !reasonValid
                ? "Reason must be at least 4 characters."
                : "Captured in audit_log under AISC-1-7-0:JOB-WORKFLOW-OVERRIDE (immutable)."
            }
            error={reason.length > 0 && !reasonValid}
          />

          {error && (
            <Alert severity="error">
              {error?.response?.data?.error || error?.message || "Override failed"}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onCancel}
          disabled={submitting}
          variant="outlined"
          sx={{
            borderRadius: 2, textTransform: "none",
            fontWeight: 600, letterSpacing: "0.01em", flex: 1,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!reasonValid || sameStep || submitting}
          variant="contained"
          sx={{
            borderRadius: 2, textTransform: "none",
            fontWeight: 600, letterSpacing: "0.01em", flex: 2,
          }}
        >
          {submitting ? "Saving…" : `Set to "${targetLabel}"`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
